import {Component, ElementRef, HostBinding, Input, OnChanges, OnInit, Renderer2, ViewChild} from '@angular/core';
import {DrawingType} from '@domain/production-schedule/drawing-type.enum';
import {ProductionScheduleItemInPathGroup} from '@domain/production-schedule/production-schedule-item-in-path-group';
import {OverviewListColoredYarnSetWithStartDent} from '@domain/textile-data/creel/overview-list-colored-yarn-set-with-start-dent';
import {AssertionUtils, BaseComponent, Color, convertUnit, LocaleUtils, TranslateService, Unit} from '@vdw/angular-component-library';
import {L10nIntlService} from 'angular-l10n';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-production-schedule-item-in-path-group-new]',
  templateUrl: './production-schedule-item-in-path-group-new.component.html',
  styleUrls: ['./production-schedule-item-in-path-group-new.component.scss']
})
export class ProductionScheduleItemInPathGroupNewComponent extends BaseComponent implements OnInit, OnChanges {
  @HostBinding('class.production-schedule-item-repetition') @Input() public isProductionScheduleItemInPathGroupRepetition: boolean;
  @Input() public productionScheduleItem: ProductionScheduleItemInPathGroup;
  @Input() public productionScheduleColoredYarnSet: OverviewListColoredYarnSetWithStartDent;
  @Input() public cutbarBeforeHeight: number;
  @Input() public cutbarAfterHeight: number;
  @Input() public drawingHeight: number;
  @Input() public drawingImageHeight: number;
  @Input() public drawingWidth: number;
  @Input() public totalHeight: number;

  @ViewChild('diagonalHatch') public diagonalHatch: ElementRef;
  @ViewChild('productionScheduleItemPathLabelContainer') public productionScheduleItemPathLabelContainer: ElementRef;
  @ViewChild('productionScheduleItemPathLabel') public productionScheduleItemPathLabel: ElementRef;
  @ViewChild('productionScheduleItemNonRecolorableLabelContainer') public productionScheduleItemNonRecolorableLabelContainer: ElementRef;
  @ViewChild('productionScheduleItemNonRecolorableLabel') public productionScheduleItemNonRecolorableLabel: ElementRef;

  public cutbarAfterVerticalPosition: number = 0;
  public drawingVerticalPosition: number = 0;
  public productionScheduleItemInPathGroupHeight: number = 0;
  public canShowProductionScheduleItemPathLabel: boolean = true;
  public canShowProductionScheduleItemNonRecolorableLabel: boolean = true;
  public canShowBorder: boolean = false;

  private readonly PRODUCTION_SCHEDULE_ITEM_LABEL_CONTAINER_PADDING: number = 10;

  public constructor(
    private readonly elementRef: ElementRef,
    private readonly renderer: Renderer2,
    private readonly l10nIntlService: L10nIntlService,
    private readonly translate: TranslateService
  ) {
    super();
  }

  public ngOnInit(): void {
    this.setCanShowBorder();
  }

  public ngOnChanges(): void {
    this.setProductionScheduleItemInPathGroupHeight();
    this.setCutbarAfterVerticalPosition();
    this.drawingVerticalPosition = this.productionScheduleItem.isRecolored ? this.cutbarBeforeHeight : 0;

    setTimeout(() => {
      this.setProductionScheduleItemInPathGroupAttributes();
      this.setProductionScheduleItemLabelContainersAttributes();
      this.setCanShowProductionScheduleItemLabels();
    });
  }

  public getPlaceholderWidthAndHeight(): number {
    return this.productionScheduleItem.technicalDimensions.heightInPicks > this.productionScheduleItem.technicalDimensions.widthInDents ? this.drawingWidth : this.drawingImageHeight;
  }

  public getPlaceholderPosition(): string {
    const placeholderWidthAndHeight = this.getPlaceholderWidthAndHeight();
    const position = -placeholderWidthAndHeight / 2;

    return `translate(${position}, ${position})`;
  }

  public getPathItemStyle(pathItem: ProductionScheduleItemInPathGroup): object {
    if (pathItem.scheduleItemType === DrawingType.MANUAL_NON_PRODUCTION_ITEM) {
      return {'background-image': 'url("assets/images/jute-background.svg")'};
    } else {
      const rgbValues = this.productionScheduleColoredYarnSet?.coloredYarnSet?.overviewListColorSet?.creelPositions[pathItem.borderColorPosition - 1]?.items[0] as Color;
      return {fill: `rgb(${rgbValues?.rgb?.red}, ${rgbValues?.rgb?.green}, ${rgbValues?.rgb?.blue})`};
    }
  }

  public getPathItemHeight(pathItem: ProductionScheduleItemInPathGroup): string {
    const technicalHeight = pathItem.technicalDimensions.heightInPicks;
    const commercialHeightInMM = pathItem.commercialDimensionsInMM.heightInMM;

    const commercialHeightInCM = this.convertValueToCommercialUnit(commercialHeightInMM, Unit.MILLIMETER, Unit.CENTIMETER);
    const formattedTechnicalHeight = LocaleUtils.formatNumber(technicalHeight, this.l10nIntlService, 2);

    const unit = this.translate.instant('GENERAL.UNIT.PICKS');

    return `${formattedTechnicalHeight} ${unit} / ${commercialHeightInCM}`;
  }

  public setCanShowProductionScheduleItemLabels(): void {
    this.canShowProductionScheduleItemPathLabel = this.canShowProductionScheduleItemLabel(this.productionScheduleItemPathLabel);
    this.canShowProductionScheduleItemNonRecolorableLabel = this.canShowProductionScheduleItemLabel(this.productionScheduleItemNonRecolorableLabel);
  }

  public setCanShowBorder(): void {
    this.canShowBorder = this.productionScheduleItem.scheduleItemType === DrawingType.BMP;
  }

  public getTooltipText(pathItem: ProductionScheduleItemInPathGroup): string {
    return `${this.translate.instant('GENERAL.DIMENSIONS.HEIGHT')}: ${this.getPathItemHeight(pathItem)}`;
  }

  public getAdjustedWidth(pixels: number): string {
    return this.drawingWidth ? `${this.drawingWidth - pixels}px` : '100%';
  }

  public getAdjustedHeight(pixels: number): string {
    let result: number;
    if (this.productionScheduleItemInPathGroupHeight) {
      result = this.productionScheduleItemInPathGroupHeight - pixels;
    }

    if (!AssertionUtils.isNullOrUndefined(result)) {
      if (result < pixels) {
        return `${pixels}px`;
      } else {
        return `${result}px`;
      }
    } else {
      return '100%';
    }
  }

  private setProductionScheduleItemInPathGroupAttributes(): void {
    const productionScheduleItemInPathGroupElement = this.elementRef.nativeElement;

    this.renderer.setAttribute(productionScheduleItemInPathGroupElement, 'height', `${this.totalHeight}`);
    this.renderer.setAttribute(productionScheduleItemInPathGroupElement, 'width', `${this.drawingWidth}`);

    this.renderer.setAttribute(productionScheduleItemInPathGroupElement, 'y', `${this.getVerticalPositionOfProductionScheduleItemInPathGroup()}`);
    this.renderer.setAttribute(productionScheduleItemInPathGroupElement, 'x', '0');
  }

  private getVerticalPositionOfProductionScheduleItemInPathGroup(): number {
    if (!AssertionUtils.isNullOrUndefined(this.elementRef.nativeElement) && this.isProductionScheduleItemInPathGroupRepetition) {
      return this.elementRef.nativeElement.previousElementSibling.getAttribute('y') - this.productionScheduleItemInPathGroupHeight;
    } else {
      return 0;
    }
  }

  private setCutbarAfterVerticalPosition(): void {
    this.cutbarAfterVerticalPosition = this.totalHeight - this.cutbarBeforeHeight;
  }

  private setProductionScheduleItemInPathGroupHeight(): void {
    this.productionScheduleItemInPathGroupHeight = this.totalHeight - this.cutbarAfterHeight - this.cutbarBeforeHeight;
  }

  private setProductionScheduleItemLabelContainersAttributes(): void {
    this.setProductionScheduleItemLabelContainerAttributes(this.productionScheduleItemPathLabel, this.productionScheduleItemPathLabelContainer);
    this.setProductionScheduleItemLabelContainerAttributes(this.productionScheduleItemNonRecolorableLabel, this.productionScheduleItemNonRecolorableLabelContainer);
  }

  private setProductionScheduleItemLabelContainerAttributes(label: ElementRef, container: ElementRef): void {
    if (AssertionUtils.isNullOrUndefined(label) || AssertionUtils.isNullOrUndefined(container) || this.totalHeight === 0) {
      return;
    }

    const textWidth = label.nativeElement.getBBox().width;
    const containerWidth = textWidth + this.PRODUCTION_SCHEDULE_ITEM_LABEL_CONTAINER_PADDING * 2;

    this.renderer.setAttribute(container.nativeElement, 'width', `${containerWidth}`);
    this.renderer.setAttribute(container.nativeElement, 'x', `calc(50% - ${containerWidth / 2}px)`);
  }

  private convertValueToCommercialUnit(value: number, fromUnit: Unit, toUnit: Unit): string {
    const convertedValue = convertUnit({
      from: {
        value: value,
        unit: fromUnit
      },
      to: toUnit
    });

    const formattedNumber = LocaleUtils.formatNumber(convertedValue, this.l10nIntlService, 2);
    const unit = this.translate.instant('GENERAL.UNIT.CENTIMETER');

    return `${formattedNumber} ${unit}`;
  }

  private canShowProductionScheduleItemLabel(element: ElementRef): boolean {
    if (AssertionUtils.isNullOrUndefined(element) || this.totalHeight === 0) {
      return;
    }

    const labelBoundingBox = element.nativeElement.getBBox();
    const labelWidth: number = labelBoundingBox.width;
    const labelHeight = labelBoundingBox.height;

    const containerWidth = labelWidth + this.PRODUCTION_SCHEDULE_ITEM_LABEL_CONTAINER_PADDING * 2;
    const containerHeight = labelHeight + this.PRODUCTION_SCHEDULE_ITEM_LABEL_CONTAINER_PADDING * 2;

    return containerWidth < this.drawingWidth && containerHeight < this.productionScheduleItemInPathGroupHeight;
  }
}
